import { Box, Grid, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import SendIcon from '@mui/icons-material/Send';
import CheckIcon from '@mui/icons-material/Check';

import FieldUniqueAnswer from '../../../../components/general/surveysField/fieldUniqueAnswer';
import FieldLinealAnswer from '../../../../components/general/surveysField/fieldLinealAnswer';
import FieldTextAnswer from '../../../../components/general/surveysField/fieldTextAnswer';
import FieldListAnswer from '../../../../components/general/surveysField/fieldUniqueAnswerList';

import { useTemporalPrevisora } from './hook';
import React from 'react';

interface Props {
    onSave: () => void;
    onChangeSurvey: (survey: any) => void;
}

export const FormRenderTemporalPrevisora = ({
    onSave,
    onChangeSurvey,
}: Props) => {
    const {
        survey,
        questions,
        answers,

        loading,
        handleChangeQuestion,
        documentValidate,
        isInvalid,
        onClickSave,

        isCedulaValid,
        questionsPersonalData,
        questionsAreasData,
        questionsSurveyData,
        questionCedula,

        isEndSurvey,
        isApplicable,
        descriptionArea,
        questionN3,
    } = useTemporalPrevisora({ onSave, onChangeSurvey });

    const gridPersonalQuestion = (q: any, index: number, document?: string) => {
        return (
            <Grid
                item
                xs={12}
                mt={2}
                key={q.id}
                alignItems="center"
                display="flex"
                flexDirection="column"
            >
                <FieldTextAnswer
                    icon={
                        index === 0 && isCedulaValid ? (
                            <CheckIcon sx={{ color: '#4CAF50' }} />
                        ) : undefined
                    }
                    key={q.id}
                    title={`${q?.position + 1}. ${q?.description}`}
                    idQuestion={q.id}
                    questionType={q.type}
                    required={q.required}
                    placeholder={q?.description}
                    onChange={handleChangeQuestion}
                    type={q.type}
                    subtype={q.subtype}
                    value={q.answer}
                    disabled={index !== 0}
                />
                
                {index === 0 && document && !isCedulaValid && (
                    <LoadingButton
                        disabled={!document.trim()}
                        type="submit"
                        onClick={() => documentValidate(document)}
                        variant="contained"
                        endIcon={<CheckIcon />}
                        sx={{
                            mt: 2,
                            borderRadius: '30px',
                            backgroundColor: '#4CAF50',
                            ':hover': {
                                backgroundColor: '#388E3C',
                            },
                        }}
                    >
                        <Typography
                            fontFamily="Lucida Sans"
                            fontSize="14px"
                            textTransform="capitalize"
                        >
                            {'Verificar Cédula'}
                        </Typography>
                    </LoadingButton>
                )}
            </Grid>
        );
    };

    const documentQuestion = questions?.find(
        (q: any) => q.id === questionCedula
    );

    const gridAreasQuestion = (q: any, index?: number) => {
        const parentAnswer =
            index === 1
                ? (answers as any)?.[questionsAreasData[0]?.id]?.answer
                : index === 2
                  ? (answers as any)?.[questionsAreasData[1]?.id]?.answer
                  : undefined;

        // Índices 1 y 2 (Gerencia / Área): vacíos hasta que cambie el select padre
        const listAnswers =
            index === 1 || index === 2
                ? parentAnswer
                    ? q?.answers || []
                    : []
                : q?.answers || [];

        return (
            <Grid item xs={12} mt={2} key={q.id}>
                {q.type === 'only' ? (
                    <FieldUniqueAnswer
                        key={q.id}
                        title={`${q.position + 1}. ${q?.description}`}
                        required={q.required}
                        answerQuestion={q?.answers || []}
                        idQuestion={q.id}
                        questionType={q.type}
                        whyQuestion={q?.why}
                        name="answer"
                        disabled={loading}
                        onChange={handleChangeQuestion}
                    />
                ) : q.type === 'list-only' ? (
                    <>
                        <FieldListAnswer
                            title={`${q.position + 1}. ${q?.description}`}
                            required={q.required}
                            answerQuestion={listAnswers}
                            idQuestion={q.id}
                            questionType={q.type}
                            whyQuestion={q?.why}
                            name="answer"
                            value={(answers as any)?.[q.id]?.answer || ''}
                            disabled={loading}
                            onChange={handleChangeQuestion}
                        />
                        <Typography
                            fontFamily="Lucida Sans"
                            textAlign="center"
                            color="#800080"
                            px={{ xs: 0, md: 12 }}
                        >
                            {q.id === questionN3 &&
                                descriptionArea &&
                                descriptionArea
                                    .split(/<br\s*\/?>/i)
                                    .map((line, index) => (
                                        <React.Fragment key={index}>
                                            {line}
                                            <br />
                                        </React.Fragment>
                                    ))}
                        </Typography>
                    </>
                ) : q.type === 'validation-reference' ? (
                    <FieldUniqueAnswer
                        key={q.id}
                        title={`${q.position + 1}. ${q?.description}`}
                        required={q.required}
                        answerQuestion={q?.answers || []}
                        idQuestion={q.id}
                        questionType={q.type}
                        whyQuestion={q?.why}
                        name="answer"
                        disabled={loading}
                        onChange={handleChangeQuestion}
                    />
                ) : (
                    <FieldTextAnswer
                        key={q.id}
                        title={`${q.position + 1}. ${q?.description}`}
                        idQuestion={q.id}
                        questionType={q.type}
                        required={q.required}
                        placeholder={q?.description}
                        onChange={handleChangeQuestion}
                        type={q.type}
                        subtype={q.subtype}
                        value={
                            q.answer
                                ? q.answer
                                : (answers as any)?.[q.id]?.answer
                        }
                        disabled={q.locked}
                    />
                )}
            </Grid>
        );
    };

    const gridSurveyQuestion = (q: any) => {
        return (
            <Grid item xs={12} mt={2} key={q.id}>
                {q.type === 'linear' ? (
                    <FieldLinealAnswer
                        key={q.id}
                        title={`${q.position + 1}. ${q?.description}`}
                        answerQuestion={q?.answers[0] || []}
                        idQuestion={q.id}
                        required={q.required}
                        questionType={q.type}
                        questionIcon={q.icon}
                        whyQuestion={q?.why}
                        name="answer"
                        disabled={loading}
                        onChange={handleChangeQuestion}
                    />
                ) : (
                    <FieldTextAnswer
                        key={q.id}
                        title={`${q.position + 1}. ${q?.description}`}
                        idQuestion={q.id}
                        questionType={q.type}
                        required={q.required}
                        placeholder={q?.description}
                        onChange={handleChangeQuestion}
                        type={q.type}
                        subtype={q.subtype}
                        value={
                            q.answer
                                ? q.answer
                                : (answers as any)?.[q.id]?.answer
                        }
                        disabled={q.locked}
                    />
                )}
            </Grid>
        );
    };

    const TextoEncuesta = () => (
        <Box
            sx={{
                p: 4,
                textAlign: 'center',
                color: '#800080',
                fontFamily: 'Lucida Sans',
                lineHeight: 1.8,
                fontSize: '16px',
            }}
        >
            <Typography
                fontFamily="Lucida Sans"
                fontSize="1.4rem"
                fontWeight="700!important"
                px={{ xs: 0, md: 12 }}
            >
                Participa en nuestra encuesta de Satisfacción Cliente Interno
            </Typography>
            <br />
            <Typography
                sx={{
                    whiteSpace: 'pre-line',
                    textAlign: 'center',
                    fontFamily: 'Lucida Sans',
                }}
            >
                {`Te invitamos a participar en nuestra Encuesta de Satisfacción Cliente Interno cuéntanos cuál fue tu nivel de satisfacción con el servicio prestado por las diferentes áreas de la compañía, durante el primer semestre del 2026, que será insumo para identificar los aspectos de mejora orientados a fortalecer el servicio.
                    Si existe algún caso puntual a resaltar o exponer, puedes comentarlo en el campo "Observaciones".`}
                <br />
                <br />
                {`Califica de forma objetiva las áreas enfatizando en la satisfacción relacionada con el servicio recibido durante el primer semestre de 2026, acorde a las funciones de cada área y cómo el mismo contribuyó al logro de tus objetivos. ¡Esto no es una evaluación de desempeño, es una valoración del servicio de cada área!`}
                <br />
                <br />
                {`Recuerda seleccionar NO aplica en el área a la cual perteneces o de lo contrario esa evaluación será eliminada.`}
            </Typography>
        </Box>
    );

    const TextoPregunta = () => (
        <Grid display="flex" justifyContent="center" width={'100%'}>
            <Box
                sx={{
                    textAlign: 'center',
                    px: { xs: 2, md: 12 },
                    mt: 3,
                }}
            >
                <Typography
                    fontFamily="Lucida Sans"
                    fontSize="1.2rem"
                    fontWeight="700!important"
                    textAlign="center"
                    color={
                        survey?.title.toLowerCase().includes('previsora')
                            ? '#E63992'
                            : '#36c'
                    }
                    px={{ xs: 0, md: 12 }}
                >
                    {`Califique de 1 a 5 las siguientes preguntas teniendo en cuenta que 5 es Muy Satisfecho y 1 es muy Insatisfecho: `}
                </Typography>
            </Box>
        </Grid>
    );
    
    return (
        <Grid container sx={{ textAlign: 'center', mt: 3 }}>
            <Grid container sx={{ textAlign: 'center', mt: 3 }}>
                {TextoEncuesta()}
            </Grid>
            {survey && (
                <Grid container sx={{ textAlign: 'center', mt: 3 }}>
                    {questionCedula &&
                        (() => {
                            const document =
                                (answers as any)?.[questionCedula]?.answer ||
                                '';
                            return (
                                <>
                                    {gridPersonalQuestion(
                                        documentQuestion,
                                        0,
                                        document
                                    )}
                                </>
                            );
                        })()}

                    {questionsPersonalData &&
                        isCedulaValid &&
                        questionsPersonalData.map((q: any, index: number) => (
                            <React.Fragment key={index}>
                                {gridPersonalQuestion(q, 1)}
                            </React.Fragment>
                        ))}

                    {isCedulaValid && (
                        <>
                            {questionsAreasData?.length > 0 &&
                                questionsAreasData.map(
                                    (q: any, index: number) => (
                                        <React.Fragment key={index}>
                                            {gridAreasQuestion(q, index)}
                                        </React.Fragment>
                                    )
                                )}
                            {isApplicable && TextoPregunta()}
                            {isApplicable &&
                                questionsSurveyData?.length > 0 &&
                                questionsSurveyData.map(
                                    (q: any, index: number) => (
                                        <React.Fragment key={index}>
                                            {gridSurveyQuestion(q)}
                                        </React.Fragment>
                                    )
                                )}
                        </>
                    )}
                </Grid>
            )}
            {survey && questions && questions.length > 0 && (
                <Grid item xs={12} mt={3}>
                    <LoadingButton
                        loading={loading}
                        disabled={isInvalid()}
                        type="submit"
                        onClick={onClickSave}
                        variant="contained"
                        endIcon={<SendIcon />}
                        sx={{
                            mb: 5,
                            height: '35px',
                            borderRadius: '30px',
                            backgroundColor: '#3C92FF',
                            ':hover': {
                                backgroundColor: '#0C53EC',
                            },
                        }}
                    >
                        <Typography
                            fontFamily="Lucida Sans"
                            fontSize="14px"
                            textTransform="capitalize"
                        >
                            {isEndSurvey ? 'Enviar Respuestas' : 'Siguiente'}
                        </Typography>
                    </LoadingButton>
                </Grid>
            )}
            {survey && sessionStorage.setItem('survey', survey?.title)}
        </Grid>
    );
};
